import clsx from 'clsx';
import { LockClosedIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatDateSafe } from '@/utils/dateUtils';
import type { HolidayEntry } from '../../../models/holiday.types';
import {
    EDIT_LABEL,
    RECURRING_NO,
    RECURRING_NO_HINT,
    RECURRING_YES,
    REMOVE_LABEL,
    LOCKED_BADGE,
    LOCKED_HINT,
    SCOPE_CLASSES,
} from '../holidayManager.definitions';

export interface HolidayCardProps {
    holiday: HolidayEntry;
    canEdit: boolean;
    onEdit: (holiday: HolidayEntry) => void;
    onRemove: (holiday: HolidayEntry) => void;
}

export function HolidayCard({ holiday, canEdit, onEdit, onRemove }: HolidayCardProps) {
    return (
        <article className="rounded-2xl border border-raro-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <span className="tabular-nums font-medium text-raro-gray-900 font-montserrat">
                    {formatDateSafe(holiday.date)}
                </span>
                <span
                    className={clsx(
                        'shrink-0 rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                        SCOPE_CLASSES[holiday.scope]
                    )}
                >
                    {holiday.scope}
                </span>
            </div>

            <p className="mt-1 font-medium text-raro-gray-800 font-montserrat">{holiday.name}</p>

            <p className="mt-1 text-xs font-montserrat">
                {holiday.recurring ? (
                    <span className="text-raro-gray-500">{RECURRING_YES}</span>
                ) : (
                    <span className="text-amber-700" title={RECURRING_NO_HINT}>
                        {RECURRING_NO}
                    </span>
                )}
            </p>

            {canEdit && (
                <div className="mt-3 border-t border-raro-gray-100 pt-3">
                    {holiday.locked ? (
                        <span
                            title={LOCKED_HINT}
                            aria-label={LOCKED_HINT}
                            className="inline-flex items-center gap-1 text-xs text-raro-gray-400 font-montserrat"
                        >
                            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            {LOCKED_BADGE[holiday.scope]}
                        </span>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => onEdit(holiday)}
                                aria-label={`${EDIT_LABEL} ${holiday.name}`}
                                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-primary-200 px-3 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 font-montserrat"
                            >
                                <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                                {EDIT_LABEL}
                            </button>
                            <button
                                type="button"
                                onClick={() => onRemove(holiday)}
                                aria-label={`${REMOVE_LABEL} ${holiday.name}`}
                                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50 font-montserrat"
                            >
                                <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                {REMOVE_LABEL}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </article>
    );
}

export default HolidayCard;
